import { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import { useState, useEffect, useRef } from 'preact/hooks'
import ItemApi from '../../ItemApi'

const defaultBoxStyles = {
  position: 'absolute',
  border: '0px solid rgb(255, 255, 255)',
  borderRadius: 0,
  boxShadow: 'rgb(0 0 0 / 20%) 0px 0px 50px 20px',
  zIndex: 2147483000,
  transform: 'translate(0px, 0px)',
  boxSizing: 'border-box',
  transition: 'opacity 150ms'
}

type Props = {
  api: ItemApi
  children: ComponentChildren
}

const Modal = (props: Props) => {
  const [isOpen, setOpen] = useState(false)
  const node = useRef<HTMLDivElement>(null)
  const closeRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const modalLayoutId = 'wf_modal_layout'

    const getLayoutElement = () => {
      const existingLayout = document.getElementById(modalLayoutId)

      if (existingLayout) return existingLayout

      const layout = document.createElement('div')
      layout.classList.add('wf_reset')
      layout.id = modalLayoutId
      layout.style.height = '100%'
      layout.style.left = '0'
      layout.style.opacity = isOpen ? '1' : '0'
      layout.style.position = 'fixed'
      layout.style.top = '0'
      layout.style.width = '100%'
      layout.style.zIndex = '10000'
      layout.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'

      return layout
    }

    const layoutElement = getLayoutElement()
    document.body.appendChild(layoutElement)

    const onClickOutside = (e: MouseEvent) => {
      if (e.target instanceof HTMLElement) {
        const ignoreList = document.getElementsByClassName('wf_skip_click_outside')
        let has = false

        for (const el of ignoreList) {
          if (el.contains(e.target)) {
            has = true
            break
          }
        }

        if (!has && !node.current?.contains(e.target)) {
          setOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', onClickOutside)

    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      layoutElement.remove()
    }
  }, [isOpen])

  useEffect(() => {
    // Check if state has initial value to prevent autoclosing when `show()` method invoked in onReady callback
    if (isOpen === null) return
    if (!node.current) return

    if (isOpen) {
      if (closeRef.current) clearTimeout(closeRef.current)

      node.current.style.opacity = '1'
      node.current.style.left = '50%'
      node.current.style.transform = 'translateX(-50%)'
    } else {
      node.current.style.opacity = '0'
      closeRef.current = setTimeout(() => {
        if (node.current) node.current.style.left = '-99999px'
      }, 150)
    }
  }, [isOpen, node.current])

  useEffect(() => {
    props.api.on('show', () => {
      setOpen(true)
    })

    props.api.on('hide', () => {
      setOpen(false)
    })
  }, [])

  const boxStyles = {
    ...defaultBoxStyles,
    height: 'auto',
    top: window.scrollY + 80,
    left: -99999,
    opacity: 0
  }

  const closeStyles = {
    position: 'absolute',
    top: '-13px',
    right: '-13px',
    width: 26,
    height: 26,
    cursor: 'pointer',
    zIndex: 1
  }

  return (
    <div className="wf_reset wf_container wf_modal_wrapper">
      <div ref={node} style={boxStyles} className="wf_reset wf_box wf_modal_box">
        <div className="wf_reset wf_modal_close" style={closeStyles} onClick={() => setOpen(false)}>
          <Close />
        </div>
        {props.children}
      </div>
    </div>
  )
}

const Close = () => (
  <svg width="26px" height="26px" viewBox="0 0 26 26">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g transform="translate(-1019.000000, -30.000000)">
        <g transform="translate(395.000000, 30.000000)">
          <g transform="translate(624.000000, 0.000000)">
            <rect className="wf_close_icon_container" x="0" y="0" width="26" height="26" rx="13"></rect>
            <g transform="translate(8.000000, 8.000000)" fillRule="nonzero" fill="currentColor">
              <path d="M5.88083333,4.99708333 L9.81083333,1.06708333 C10.055,0.823125 10.055,0.427291667 9.81083333,0.183333333 C9.56666667,-0.0608333333 9.17125,-0.0608333333 8.92708333,0.183333333 L4.99708333,4.11333333 L1.066875,0.183333333 C0.822708333,-0.0608333333 0.427291667,-0.0608333333 0.183125,0.183333333 C-0.0610416667,0.427291667 -0.0610416667,0.823125 0.183125,1.06708333 L4.11333333,4.99708333 L0.183125,8.92708333 C-0.0610416667,9.17104167 -0.0610416667,9.566875 0.183125,9.81083333 C0.305208333,9.93270833 0.465208333,9.99375 0.625,9.99375 C0.784791667,9.99375 0.944791667,9.93270833 1.066875,9.810625 L4.99708333,5.880625 L8.92708333,9.810625 C9.04916667,9.93270833 9.20916667,9.99375 9.36895833,9.99375 C9.52875,9.99375 9.68875,9.93270833 9.81083333,9.810625 C10.055,9.56666667 10.055,9.17083333 9.81083333,8.926875 L5.88083333,4.99708333 Z"></path>
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
)

export default memo(Modal)
